import { NfcTagRequestStatus } from "@clipboard-health/contract-backend-main";
import { apiClient } from "@src/appV2/api/api";
import {
  type NfcTag,
  type SaveNfcTagLocationParams,
} from "@src/appV2/FacilityProfile/AttendanceVerificationMethods/types";
import { useTu } from "@src/appV2/i18n/useTu";
import { useToast } from "@src/appV2/lib";
import { useCallback, useEffect, useState } from "react";

interface NfcTagRequestItem {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _id: string;
  tagStatus: string;
  tagLocation?: string;
}

interface NfcTagRequestsResponse {
  nfcTagRequests: NfcTagRequestItem[];
}

interface UseNfcTagsParams {
  workplaceId: string;
}

interface UseNfcTagsResult {
  nfcRequestList: NfcTag[];
  onDeleteNfcTag: (tagId: string) => Promise<void>;
  onRequestNfcTags: (numberOfTags: number) => Promise<void>;
  onSaveNfcTagLocation: (parameters: SaveNfcTagLocationParams) => Promise<void>;
}

export function useNfcTags({ workplaceId }: UseNfcTagsParams): UseNfcTagsResult {
  const { tu } = useTu("settings");
  const { showSuccessToast, showErrorToast } = useToast();

  const [nfcRequestList, setNfcRequestList] = useState<NfcTag[]>([]);
  const [hasExistingRecord, setHasExistingRecord] = useState(false);

  const fetchNfcTags = useCallback(async () => {
    if (!workplaceId) {
      return;
    }

    try {
      const response = await apiClient.get<NfcTagRequestsResponse>(
        `/facility/nfcTagRequests/${workplaceId}`
      );

      const allTags = response.data.nfcTagRequests ?? [];
      setHasExistingRecord(allTags.length > 0);

      const nonDeletedTags: NfcTag[] = allTags
        .filter((tag) => tag.tagStatus !== NfcTagRequestStatus.DELETED)
        .map((tag, index) => ({
          tagId: tag._id,
          tagNumber: index + 1,
          tagStatus: tag.tagStatus as keyof typeof NfcTagRequestStatus,
          tagLocation: tag.tagLocation ?? "",
        }));

      setNfcRequestList(nonDeletedTags);
    } catch {
      // Silently fail — the AttendanceVerificationMethods component handles missing NFC data gracefully
    }
  }, [workplaceId]);

  useEffect(() => {
    void fetchNfcTags();
  }, [fetchNfcTags]);

  const onSaveNfcTagLocation = useCallback(
    async ({ tagId, location, currentStatus }: SaveNfcTagLocationParams) => {
      try {
        const endpoint =
          currentStatus !== NfcTagRequestStatus.SET_UP
            ? "/facility/nfcTagRequests/status"
            : "/facility/nfcTagRequests/location";

        const payload =
          currentStatus !== NfcTagRequestStatus.SET_UP
            ? {
                facilityId: workplaceId,
                nfcTagRequests: [
                  { tagId, tagStatus: NfcTagRequestStatus.SET_UP, tagLocation: location },
                ],
              }
            : { facilityId: workplaceId, tagId, tagLocation: location };

        await apiClient.put(endpoint, payload);

        showSuccessToast(tu("Location updated successfully"));
        await fetchNfcTags();
      } catch {
        showErrorToast(tu("Failed to update NFC tag location."));
      }
    },
    [workplaceId, fetchNfcTags, showSuccessToast, showErrorToast, tu]
  );

  const onDeleteNfcTag = useCallback(
    async (tagId: string) => {
      try {
        await apiClient.put("/facility/nfcTagRequests/status", {
          facilityId: workplaceId,
          nfcTagRequests: [{ tagId, tagStatus: NfcTagRequestStatus.DELETED }],
        });
        showSuccessToast(tu("Tag deleted successfully"));
        await fetchNfcTags();
      } catch {
        showErrorToast(tu("Failed to delete NFC tag."));
      }
    },
    [workplaceId, fetchNfcTags, showSuccessToast, showErrorToast, tu]
  );

  const onRequestNfcTags = useCallback(
    async (numberOfTags: number) => {
      try {
        const requestData = { facilityId: workplaceId, numberOfTagsRequested: numberOfTags };
        await (hasExistingRecord
          ? apiClient.put("/facility/nfcTagRequests", requestData)
          : apiClient.post("/facility/nfcTagRequests", requestData));

        showSuccessToast(tu("Tags requested successfully"));
        await fetchNfcTags();
      } catch {
        showErrorToast(tu("Failed to request NFC tags."));
      }
    },
    [workplaceId, hasExistingRecord, fetchNfcTags, showSuccessToast, showErrorToast, tu]
  );

  return { nfcRequestList, onSaveNfcTagLocation, onDeleteNfcTag, onRequestNfcTags };
}
